import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';

import {basicSetup} from "codemirror";
import {EditorView, Decoration, DecorationSet, ViewPlugin, ViewUpdate, MatchDecorator} from '@codemirror/view';
import {html} from "@codemirror/lang-html";
import {EditorState, Extension} from "@codemirror/state";

// Shift
@Component({
    selector: 'code-mirror-editor',
    templateUrl: './code-mirror-editor.component.html',
    styleUrls: ['./code-mirror-editor.component.scss'],
    standalone: false
})
export class CodeMirrorEditorComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

    busy: boolean;  // indicator whether the component is busy
    initialized: boolean; // indicator whether the component was initialized


    @Input() editorContent: string;
    @Output() contentChanged = new EventEmitter<string>();
    @Output() onDrop = new EventEmitter<DragEvent>();
    // @ViewChild('codeMirrorHost') private codeMirrorHost: ElementRef | undefined;
    @ViewChild('editor') myEditor: any;
    private view!: EditorView;

    /**
     * FreeMarker syntax highlighting decorations for CodeMirror 6.
     * - ${...} expressions are highlighted in blue
     * - <#if>, <#list>, <#else>, </#if>, </#list> directives in green
     * - #artefact:N# references in purple
     */
    private freeMarkerHighlighter = this.createFreeMarkerHighlighter();

    constructor() {
    }

    ngOnInit(): void {
        this.initComponent();

    }

    ngAfterViewInit(): void {
        let myEditorElement = this.myEditor.nativeElement;
        let myExt: Extension = [basicSetup, html(), this.freeMarkerHighlighter];
        let state!: EditorState;

        try {
            state = EditorState.create({
                doc: this.editorContent,
                extensions: [
                    myExt,
                    EditorView.updateListener.of(update => {
                        if (update.docChanged) {
                            this.contentChanged.emit(update.state.doc.toString());
                        }
                    }),
                    EditorView.domEventHandlers({
                        drop: (event, view) => {
                            event.preventDefault();
                            this.onDrop.emit(event);
                            return true;
                        }
                    })
                ],
            });
        } catch (e) {
            console.error(e);
        }

        this.view = new EditorView({
            state,
            parent: myEditorElement,
        });
    }

    /**
     * Insert text at current cursor/selection
     */
    insertTextIntoPosition(value: string): void {
        if (!this.view) return;

        const { state } = this.view;
        const transaction = state.update({
            changes: {
                from: state.selection.main.from,
                to: state.selection.main.to,
                insert: value
            },
            selection: { anchor: state.selection.main.from + value.length }
        });

        this.view.dispatch(transaction);
        this.contentChanged.emit(this.view.state.doc.toString());
    }

    /**
     * Public method for inserting text at the current cursor position.
     * Used by the TeEditorCommunicationService to insert variables, artefacts, etc.
     * @param text The text/code to insert
     */
    insertAtCursor(text: string): void {
        this.insertTextIntoPosition(text);
    }

    /**
     * Create FreeMarker syntax highlighting extension for CodeMirror 6.
     * Uses MatchDecorator to apply CSS classes to FreeMarker patterns.
     */
    private createFreeMarkerHighlighter(): Extension {
        const variableDeco = Decoration.mark({ class: 'cm-te-variable' });
        const tagDeco = Decoration.mark({ class: 'cm-te-tag' });
        const artefactDeco = Decoration.mark({ class: 'cm-te-artefact' });

        // Match FreeMarker variable expressions: ${...}
        const variableMatcher = new MatchDecorator({
            regexp: /\$\{[^}]+\}/g,
            decoration: variableDeco
        });

        // Match FreeMarker directives: <#if...>, <#else>, </#if>, <#list...>, </#list>, <#elseif...>
        const tagMatcher = new MatchDecorator({
            regexp: /<#(?:if|else|elseif|list|assign|macro|function|include|import|switch|case|break|default|return|stop|flush|nested|recurse|visit|setting|ftl)\b[^>]*>|<\/#(?:if|list|assign|macro|function|switch)>/g,
            decoration: tagDeco
        });

        // Match artefact references: #artefact:N#
        const artefactMatcher = new MatchDecorator({
            regexp: /#artefact:\d+#/g,
            decoration: artefactDeco
        });

        const variablePlugin = ViewPlugin.fromClass(class {
            decorations: DecorationSet;
            constructor(view: EditorView) { this.decorations = variableMatcher.createDeco(view); }
            update(update: ViewUpdate) { this.decorations = variableMatcher.updateDeco(update, this.decorations); }
        }, { decorations: v => v.decorations });

        const tagPlugin = ViewPlugin.fromClass(class {
            decorations: DecorationSet;
            constructor(view: EditorView) { this.decorations = tagMatcher.createDeco(view); }
            update(update: ViewUpdate) { this.decorations = tagMatcher.updateDeco(update, this.decorations); }
        }, { decorations: v => v.decorations });

        const artefactPlugin = ViewPlugin.fromClass(class {
            decorations: DecorationSet;
            constructor(view: EditorView) { this.decorations = artefactMatcher.createDeco(view); }
            update(update: ViewUpdate) { this.decorations = artefactMatcher.updateDeco(update, this.decorations); }
        }, { decorations: v => v.decorations });

        // Theme for the highlight classes
        const highlightTheme = EditorView.baseTheme({
            '.cm-te-variable': {
                color: '#1967d2',
                backgroundColor: '#e8f0fe',
                borderRadius: '3px',
                padding: '0 2px'
            },
            '.cm-te-tag': {
                color: '#047857',
                backgroundColor: '#e8faf0',
                borderRadius: '3px',
                padding: '0 2px',
                fontWeight: '600'
            },
            '.cm-te-artefact': {
                color: '#7c3aed',
                backgroundColor: '#f0e6ff',
                borderRadius: '3px',
                padding: '0 2px',
                fontWeight: '600'
            }
        });

        return [variablePlugin, tagPlugin, artefactPlugin, highlightTheme];
    }

    /**
     * Initialize Component.
     */
    initComponent() {
        this.refreshComponent();
    }

    /**
     * Refresh Component.
     */
    refreshComponent() {
        this.initialized = true;
    }

    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {

        if (!this.initialized) {
            return;
        }

        if (changes["editorContent"]) {
            this.refreshComponent();
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {

    }
}
